// Renders Learn Q&A answer text: inline [label]{link} tokens, then markdown (marked).
// Mirrors marketplace / downloads post-render hooks (copy, external links, math).

import React, { useMemo, useEffect, useRef } from 'react';
import { parseMarkdown, setupCodeBlockCopyButtons, setupExternalLinkHandlers, renderMath } from '../../../core';
import { linkUtils } from '../../../core/utils';

/**
 * @param {() => void} [onContentRendered] — Called after markdown HTML is applied and post-processors run (layout-ready).
 */
export function LearnQaAnswerMarkdown({ qa, className, onContentRendered }) {
  const ref = useRef(null);
  const onContentRenderedRef = useRef(onContentRendered);
  onContentRenderedRef.current = onContentRendered;

  const html = useMemo(() => {
    if (!qa) return '';
    const { processedText } = linkUtils.processInlineLinks(qa.answer || '', qa.links || []);
    return parseMarkdown(processedText);
  }, [qa]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!html) {
      el.innerHTML = '';
      onContentRenderedRef.current?.();
      return;
    }
    el.innerHTML = html;
    setupCodeBlockCopyButtons(el);
    setupExternalLinkHandlers(el);
    renderMath(el);
    // Let layout + KaTeX settle before parents measure overflow (scrollHeight / clientHeight).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        onContentRenderedRef.current?.();
      });
    });
  }, [html]);

  return <div ref={ref} className={className} />;
}
